import { useState, useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { authorsApi } from '../api/authors';
import { useDebounce } from './useDebounce';

/**
 * Custom hook for author search functionality with debouncing, memoization, and pagination
 * 
 * @returns {Object} Search state and handlers with pagination support
 */
export function useAuthorSearch() {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Query for authors list with debounced search and pagination
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingAuthors,
    error
  } = useInfiniteQuery({
    queryKey: ['authors', debouncedSearchQuery],
    queryFn: ({ pageParam }) => {
      if (debouncedSearchQuery.trim()) {
        return authorsApi.search(debouncedSearchQuery.trim(), 10);
      }
      return authorsApi.list({ limit: '10', cursor: pageParam });
    },
    getNextPageParam: (lastPage) => {
      // Return the cursor for the next page, or undefined if no more pages
      return lastPage?.hasMore ? lastPage.cursor : undefined;
    },
    initialPageParam: undefined,
  });

  // Flatten all pages of authors into a single array
  const authorsData = useMemo(() => {
    if (!data?.pages) {
      return { authors: [], hasMore: false };
    }

    const allAuthors = data.pages.flatMap(page => page.authors || []);
    const hasMore = data.pages[data.pages.length - 1]?.hasMore || false;

    return {
      authors: allAuthors,
      hasMore
    };
  }, [data]);

  // Memoized handlers
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Memoized return object to prevent unnecessary re-renders
  return useMemo(() => ({
    searchQuery,
    debouncedSearchQuery,
    authorsData,
    isLoadingAuthors,
    handleSearchChange,
    handleClearSearch,
    handleLoadMore,
    hasNextPage,
    isFetchingNextPage,
    error
  }), [searchQuery, debouncedSearchQuery, authorsData, isLoadingAuthors, handleSearchChange, handleClearSearch, handleLoadMore, hasNextPage, isFetchingNextPage, error]);
}

export default useAuthorSearch;
